import React from 'react';

import {
  ArrowRightCircle,
  GraduationCapIcon,
  MoreHorizontalIcon,
} from 'lucide-react';

import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sandtech-hub-monorepo/ui-kit';
import { useMutation } from '@apollo/client';

import {
  GetViewerNotificationsQuery,
  MarkNotificationsAsDismissedDocument,
  MarkNotificationsAsSeenDocument,
  NotificationType,
} from '@sandtech-hub-monorepo/ehub-client';
import { HeaderLink } from './HeaderLink';
import { usePlatforms } from '@sandtech-hub-monorepo/ui-kit-site';

const notificationsPageCount = 5;
export const NotificationItem = ({
  notification,
  sdk,
}: {
  notification: GetViewerNotificationsQuery['viewer']['notifications']['edges'][number]['node'];
  sdk: boolean;
}) => {
  const { all } = usePlatforms(process.env['NX_COOKIE_DOMAIN'] || '');
  const [dismissNotification] = useMutation(
    MarkNotificationsAsDismissedDocument
  );

  const [readNotification] = useMutation(MarkNotificationsAsSeenDocument);

  const notificationHandler = (
    notification: GetViewerNotificationsQuery['viewer']['notifications']['edges'][number]['node']
  ) => {
    if (
      notification.type === NotificationType.EhubNewProgramEnrollment &&
      notification.payload.__typename ===
        'EHubNewProgramEnrollmentNotificationPayload'
    ) {
      return {
        title: "You've been accepted",
        description: `Congratulations! You've been accepted into ${notification.payload.programEnrollment?.program.name}. Access eHub to get started.`,
        cta: {
          text: `Go to Learning`,
          href: '/learning',
        },
      };
    }
    if (
      notification.type === NotificationType.EhubNewClassEnrollment &&
      notification.payload.__typename ===
        'EHubNewClassEnrollmentNotificationPayload'
    ) {
      return {
        title: `You have been enrolled into ${notification.payload.classEnrollment?.courseEnrollment.course.abbreviation}`,
        description: `We are pleased to enrol you onto ${notification.payload.classEnrollment?.courseEnrollment.course.name} - navigate to the learning centre and prepare to start your learning.`,
        cta: {
          text: `Go to ${notification.payload.classEnrollment?.courseEnrollment.course.abbreviation}`,
          href:
            notification.payload.classEnrollment?.lmsClassLocation.courseLink ??
            '/',
        },
      };
    }
    if (notification.type === NotificationType.EhubOnboardingCompleted) {
      return {
        title: 'Onboarding Completed',
        description: `Congratulations you have completed onboarding.`,
        cta: {
          text: `Go to Rewards`,
          href: all.rewards.origin,
        },
      };
    }
    if (notification.type === NotificationType.JoinCommunity) {
      return {
        title: 'Join your community',
        description:
          "Welcome to the eHub. Connect with your peers in your community space. It's a great place to share and learn together.",
        cta: {
          text: 'Go to community',
          href: '/community',
        },
        icon: <GraduationCapIcon className="m-2 w-6 h-6 text-primary" />,
      };
    }
    if (
      notification.type === NotificationType.PaymentIsDueSoon &&
      notification.payload.__typename === 'PaymentIsDueSoonPayload'
    ) {
      const paymentId = notification.payload.paymentId;

      return {
        title: 'Payment due soon',
        description:
          "A payment is due soon. Please ensure that it's processed to avoid any interruptions.",
        cta: {
          text: 'Make a payment',
          href:
            notification.payload.paymentPlan?.payments.find(
              (id) => id + '' === paymentId
            )?.url ?? '/',
        },
        icon: <GraduationCapIcon className="m-2 w-6 h-6 text-primary" />,
      };
    }
    return {
      title: "You've been accepted",
      description: `Congratulations! Access eHub to get started.`,
      // ctaText: 'Go to the eHub',
      cta: {
        text: `Go to Learning`,
        href: '/learning',
      },
    };
  };

  return (
    <li className="relative">
      <Card className="min-h-[122px] flex gap-2 sm:gap-6 p-2 hover:bg-muted rounded-sm transition-colors cursor-pointer shadow-sm">
        <div className="flex h-10 bg-accent-foreground rounded-full">
          <GraduationCapIcon className="m-2 w-6 h-6 text-primary" />
        </div>
        <div className="flex flex-col justify-between">
          <div
            className={`text-md text-foreground ${
              notification?.seen ? 'font-light' : 'font-medium'
            }`}
          >
            {notificationHandler(notification).title}
          </div>
          <div
            className={`text-sm pl-0 text-muted-foreground ${
              notification?.seen ? 'font-light' : 'font-medium'
            }`}
          >
            {notificationHandler(notification).description}
          </div>
          {notificationHandler(notification).cta && (
            <HeaderLink
              sdk={sdk}
              href={notificationHandler(notification).cta!.href}
            >
              <Button
                variant="link"
                className={`flex self-start gap-2 pl-0 hover:no-underline ${
                  notification?.seen ? 'font-light' : 'font-medium'
                }`}
              >
                {notificationHandler(notification).cta!.text}
                <ArrowRightCircle className="h-5 w-5" />
              </Button>
            </HeaderLink>
          )}
        </div>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger
              onMouseEnter={(e) => {
                e.stopPropagation();
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreHorizontalIcon
                size={40}
                className="bg-card hover:bg-muted bg-opacity-10 transition-colors cursor-pointer rounded-full border p-2"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              onMouseEnter={(e) => {
                e.stopPropagation();
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
              }}
              className="w-min h-min"
              align="end"
            >
              <DropdownMenuItem
                onClick={() =>
                  dismissNotification({
                    variables: {
                      input: { ids: [notification.id] },
                      first: notificationsPageCount,
                    },
                  })
                }
              >
                Dismiss
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  readNotification({
                    variables: {
                      input: { ids: [notification.id] },
                      first: notificationsPageCount,
                    },
                  })
                }
              >
                Mark as read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Card>
    </li>
  );
};
